import { Card, Table, Tag, Button, Space, Typography, message } from 'antd';
import { useState } from 'react';
import { useFederationPeers } from '../../hooks/use_federation_peers';
import { useNodeInfo } from '../../hooks/use_node_info';
import NodeAvatar from '../Node/NodeAvatar';

const { Title, Text } = Typography;

// Maps a peer status to an Ant Design Tag color (theme tokens, no hardcoded hex).
const STATUS_COLOR = { approved: 'green', pending: 'gold', blocked: 'red' };

/**
 * Superadmin-only panel to authorize which peers may link to this node via
 * federation. Renders nothing for non-superadmins (the list endpoint 403s), nor
 * on a node with federation off — there is nothing to peer with — so it is safe
 * to mount unconditionally.
 */
function FederationPeers() {
    const { peers, isServerAdmin, loading, approve, block, unblock } = useFederationPeers();
    const { federationEnabled } = useNodeInfo();
    const [busy, setBusy] = useState(null); // server_id currently mutating

    // Hide entirely until we know the caller is the server admin, and on a node
    // that isn't federated at all.
    if (!isServerAdmin || !federationEnabled) return null;

    const run = (serverId, action, fn) => {
        setBusy(serverId);
        fn(serverId)
            .then(() => message.success(`Peer ${serverId} ${action}`))
            .catch(() => message.error(`Could not ${action} ${serverId}`))
            .finally(() => setBusy(null));
    };

    const columns = [
        {
            title: 'Peer',
            dataIndex: 'server_id',
            key: 'server_id',
            render: (id, row) => (
                <Space align="center">
                    <NodeAvatar serverId={id} size={32} />
                    <div>
                        <div><Text strong>{id}</Text></div>
                        {row.base_url && <Text type="secondary" style={{ fontSize: 12 }}>{row.base_url}</Text>}
                    </div>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: status => <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => {
                const isBusy = busy === row.server_id;
                return (
                    <Space>
                        {row.status !== 'approved' && (
                            <Button size="small" type="primary" loading={isBusy}
                                onClick={() => run(row.server_id, 'approved', approve)}>
                                Approve
                            </Button>
                        )}
                        {row.status !== 'blocked' && (
                            <Button size="small" danger loading={isBusy}
                                onClick={() => run(row.server_id, 'blocked', block)}>
                                Block
                            </Button>
                        )}
                        {row.status === 'blocked' && (
                            <Button size="small" loading={isBusy}
                                onClick={() => run(row.server_id, 'unblocked', unblock)}>
                                Unblock
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Federation peers</Title>
            <Text type="secondary">
                Control which nodes may link to this server. Only approved peers can search,
                connect, message, or join hangouts here.
            </Text>
            <Table
                style={{ marginTop: 16 }}
                rowKey="server_id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={peers}
                pagination={false}
                locale={{ emptyText: 'No known peers' }}
            />
        </Card>
    );
}

export default FederationPeers;
